import { useMemo, useRef, useState } from 'react';
import {
  CustomPdfScript,
  DocumentTypes,
  type EditablePdfButtonGroup,
  type UploadedDocument,
} from '@urbanx/agx-ui-components';
import {
  type AgxDialogControlProps,
  AgxModal,
  AgxUploadedDocumentAutomationPdfEditor,
} from '@urbanx/agx.ui-components2';
import { preparePdfForEditing } from 'services';

import { makeAutomationConfiguration } from '../automationAdapter';

interface Props extends AgxDialogControlProps {
  campaignId: string;
  uploadedDocument: UploadedDocument;
  buttonGroups: EditablePdfButtonGroup[];
  customScript?: CustomPdfScript;
  getAuthToken: () => Promise<string | void>;
  getFileLink?: (_authToken: string, _filePath: string) => Promise<string | undefined>;
  editFile?: (..._args: any[]) => Promise<any>;
  onFieldsUpdated: (_fields: string[]) => void;
  onDocumentSaved?: () => void;
}

const EditPdfDialog = ({
  campaignId,
  uploadedDocument,
  buttonGroups,
  customScript = CustomPdfScript.None,
  getAuthToken,
  getFileLink,
  editFile,
  onFieldsUpdated,
  onDocumentSaved,
  isOpen,
  setIsDialogOpen,
}: Props) => {
  const [error, setError] = useState<unknown>();
  const editingFieldsRef = useRef<string[]>([]);
  const configuration = useMemo(
    () => makeAutomationConfiguration(buttonGroups, customScript),
    [buttonGroups, customScript]
  );

  if (!isOpen) return null;

  const close = () => setIsDialogOpen(false);

  const loadFileLink = async (filePath: string) => {
    const authToken = await getAuthToken();

    if (!authToken || !getFileLink) {
      throw new Error('Unable to load PDF: file access is not configured.');
    }

    return getFileLink(authToken, filePath);
  };

  const uploadEditedPdf = async ({ file }: { file: File }) => {
    const authToken = await getAuthToken();

    if (!authToken || !editFile) {
      throw new Error('Unable to save PDF: file editing is not configured.');
    }

    const formData = new FormData();
    formData.append('file', file, uploadedDocument.fileName);
    const editedDocument = await editFile(
      authToken,
      formData,
      campaignId,
      uploadedDocument.documentType,
      uploadedDocument.containerFilePath
    );

    if (!editedDocument) {
      throw new Error('Unable to save PDF.');
    }

    onFieldsUpdated(editingFieldsRef.current);
    return uploadedDocument;
  };

  const prepareDownloadedPdf = async (pdfBytes: Uint8Array, signal?: AbortSignal) => {
    const authToken = await getAuthToken();

    if (!authToken) {
      throw new Error('Unable to prepare PDF: authentication is not configured.');
    }

    return preparePdfForEditing(authToken, pdfBytes, signal);
  };

  return (
    <AgxModal
      id="edit-pdf-dialog"
      isOpen={isOpen}
      title={
        uploadedDocument.documentType === DocumentTypes.SellerDisclosure
          ? "Seller's Disclosure"
          : 'Edit PDF'
      }
      type="fullScreen"
      variant="withDivider"
      actionButtonContainerStyle="withoutDivider"
      setIsDialogOpen={setIsDialogOpen}
      content={
        <>
          {error ? (
            <div role="alert">{error instanceof Error ? error.message : 'Unable to edit PDF.'}</div>
          ) : null}
          <AgxUploadedDocumentAutomationPdfEditor
            id="editable-pdf-automation-editor"
            uploadedDocument={uploadedDocument}
            fieldGroups={configuration.fieldGroups}
            placements={configuration.placements}
            getFileLink={loadFileLink}
            preparePdfForEditing={prepareDownloadedPdf}
            showActionButtons
            saveButtonText="Save"
            cancelButtonText="Close"
            uploadEditedPdf={uploadEditedPdf}
            onFieldsChange={(fields) => {
              editingFieldsRef.current = fields;
            }}
            onCancel={close}
            onSaved={() => {
              onDocumentSaved?.();
              close();
            }}
            onError={setError}
          />
        </>
      }
    />
  );
};

export default EditPdfDialog;
